import React from "react";
import { ContainerProps, Footer, Header, LoadingV2, PrimaryButton, AppToasterV2, dialogStore } from "@ucl/library";
import { useAuthorizedCreditReportResultFlyout } from "@/hooks/useAuthorizedCreditReportResultFlyout";
import { ActionButtons } from "@ucl/library";
import { CopyUrlComponent } from "@/components/ShareApplicationWorkflow/CopyUrlComponent";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import variables from "@/config/variables";
import "./AuthorizedCreditReportResultFlyout.scss";

export interface AuthorizedCreditReportResultFlyoutProps extends ContainerProps {
  creditAuthorizationId: string;
  financialApplicationId?: string;
  isApplicantLinked?: string;
}

export const AuthorizedCreditReportResultFlyout: React.FC<AuthorizedCreditReportResultFlyoutProps> = ({
  creditAuthorizationId,
  financialApplicationId,
  isApplicantLinked = "false",
  onExit,
}) => {
  // Convert string parameter to boolean
  const isApplicantLinkedBoolean = isApplicantLinked === 'true';
  const { creditReportResult, loading, error, downloadPdf } = useAuthorizedCreditReportResultFlyout(creditAuthorizationId);
  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);

  const handleSendEmail = () => {
    if (!financialApplicationId) {
      AppToasterV2.showFailedActionToaster("Financial application ID is required to send email");
      return;
    }

    if (!creditReportResult?.creditAuthorization?.authorizedPersonName) {
      AppToasterV2.showFailedActionToaster("Authorized person name is not available");
      return;
    }

    dialogStore.openConfirmDialog({
      dialogId: "send-credit-authorization-email-confirmation",
      headerText: "Send Credit Authorization Email",
      content: (
        <p>
          Are you sure you want to send the credit authorization email to <strong>{creditReportResult.creditAuthorization.authorizedPersonName}</strong>?
        </p>
      ),
      confirmButtonText: "Send Email",
      loadingProgressContent: "Sending...",
      loadingSuccessContent: "Sending...",
      onConfirm: async () => {
        const result = {
          isSuccess: true,
          error: undefined as any,
        };

        try {
          await financialApplicationApiClient.sendCreditAuthorizationEmail(financialApplicationId, {
            creditAuthorizationId: creditAuthorizationId,
          });
          AppToasterV2.showSuccessAppToaster("Credit authorization email sent successfully");
        } catch (error: any) {
          console.error("Failed to send credit authorization email:", error);
          result.isSuccess = false;
          result.error = error;
          AppToasterV2.showFailedActionToaster(
            error?.response?.data?.detail ?? "Failed to send credit authorization email"
          );
        }

        return result;
      },
    });
  };

  // Helper function to format currency with commas and 2 decimal places
  const formatCurrency = (amount: string | null | undefined): string => {
    if (!amount) return "N/A";
    const num = parseFloat(amount);
    if (isNaN(num)) return "N/A";
    return num.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  // Helper function to format dates consistently
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "N/A";
    try {
      // Parse the date string and format it consistently
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      
      // Use UTC methods to avoid timezone issues
      const month = date.getUTCMonth() + 1; // getUTCMonth() returns 0-11
      const day = date.getUTCDate();
      const year = date.getUTCFullYear();
      
      return `${month}/${day}/${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  if (loading) {
    return (
      <div className="authorized-credit-report-result-flyout">
        <Header>Credit Report Results</Header>
        <LoadingV2 />
      </div>
    );
  }

  if (error) {
    return (
      <div className="authorized-credit-report-result-flyout">
        <Header>Credit Report Results</Header>
        <p style={{ color: "red" }}>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="authorized-credit-report-result-flyout">
      <Header>Credit Report Results</Header>
      <div style={{ padding: "20px" }}>
        {/* Credit Authorization Section */}
        <div style={{ marginBottom: "30px" }}>
          <h3 style={{ marginBottom: "15px", color: "#333", borderBottom: "1px solid #dee2e6", paddingBottom: "5px" }}>
            Credit Authorization
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
            <div>
              <strong>Authorized Person:</strong>
              <p style={{ margin: "5px 0", color: "#666" }}>
                {creditReportResult?.creditAuthorization?.authorizedPersonName || "N/A"}
              </p>
            </div>
            <div>
              <strong>Authorized Email:</strong>
              <p style={{ margin: "5px 0", color: "#666" }}>
                {creditReportResult?.creditAuthorization?.authorizedPersonEmail || "N/A"}
              </p>
            </div>
            <div>
              <strong>Authorized At:</strong>
              <p style={{ margin: "5px 0", color: "#666" }}>
                {creditReportResult?.creditAuthorization?.authorizedAt 
                  ? new Date(creditReportResult.creditAuthorization.authorizedAt).toLocaleString()
                  : "N/A"}
              </p>
            </div>
            <div>
              <strong>Decision:</strong>
              <p style={{ margin: "5px 0", color: "#666" }}>
                {creditReportResult?.creditAuthorization?.decision_AsString || "N/A"}
              </p>
            </div>
          </div>
          
          {/* Credit Authorization Link */}
          <div style={{ marginTop: "20px" }}>
            <div style={{ display: "flex", alignItems: "end", gap: "15px", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: "300px" }}>
                <CopyUrlComponent
                  url={`${variables.uiBaseUrl}/credit-authorization/${creditAuthorizationId}`}
                  label="Credit Authorization Link"
                  description="Share this link with the customer to allow them to authorize the credit check"
                />
              </div>
              <div style={{ flexShrink: 0 }}>
                <PrimaryButton
                  text="Send Email"
                  onClick={handleSendEmail}
                  disabled={!isApplicantLinkedBoolean || !financialApplicationId}
                  style={{ minWidth: "120px", marginBottom: "0" }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Credit Report Metadata Section */}
        <div style={{ marginBottom: "30px" }}>
          <h3 style={{ marginBottom: "15px", color: "#333", borderBottom: "1px solid #dee2e6", paddingBottom: "5px" }}>
            Credit Report Metadata
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
            <div>
              <strong>Score:</strong>
              <p style={{ margin: "5px 0", color: "#666" }}>
                {creditReportResult?.creditReportMetadata?.score || 
                 (creditReportResult?.crsCreditReportResult?.scores?.[0]?.noScoreReasonType 
                   ? `N/A (${creditReportResult.crsCreditReportResult.scores[0].noScoreReasonType})` 
                   : "N/A")}
              </p>
            </div>
            <div>
              <strong>Score Model:</strong>
              <p style={{ margin: "5px 0", color: "#666" }}>
                {creditReportResult?.creditReportMetadata?.scoreModel || "N/A"}
              </p>
            </div>
            <div>
              <strong>Bureau:</strong>
              <p style={{ margin: "5px 0", color: "#666" }}>
                {creditReportResult?.creditReportMetadata?.bureau || "N/A"}
              </p>
            </div>
            <div>
              <strong>Date Requested:</strong>
              <p style={{ margin: "5px 0", color: "#666" }}>
                {creditReportResult?.creditReportMetadata?.dateRequested 
                  ? new Date(creditReportResult.creditReportMetadata.dateRequested).toLocaleString()
                  : "N/A"}
              </p>
            </div>
            <div>
              <strong>Is Credit File Frozen:</strong>
              <p style={{ margin: "5px 0", color: "#666" }}>
                {creditReportResult?.creditReportMetadata?.isCreditFileFrozen ? "Yes" : "No"}
              </p>
            </div>
            <div>
              <strong>Credit File Result Status:</strong>
              <p style={{ margin: "5px 0", color: "#666" }}>
                {creditReportResult?.creditReportMetadata?.creditFileResultStatus || "N/A"}
              </p>
            </div>
          </div>
          <div style={{ marginTop: "15px" }}>
            <strong>Request Data Utilized:</strong>
            <div style={{ 
              marginTop: "5px", 
              padding: "10px", 
              backgroundColor: "#f8f9fa", 
              border: "1px solid #dee2e6", 
              borderRadius: "4px",
              fontFamily: "monospace",
              fontSize: "12px",
              maxHeight: "200px",
              overflow: "auto"
            }}>
              <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {creditReportResult?.creditReportMetadata?.requestDataJson 
                  ? JSON.stringify(JSON.parse(creditReportResult.creditReportMetadata.requestDataJson), null, 2)
                  : "N/A"}
              </pre>
            </div>
          </div>
        </div>

        {/* CRS Credit Report Result Section */}
        <div>
          <h3 style={{ marginBottom: "15px", color: "#333", borderBottom: "1px solid #dee2e6", paddingBottom: "5px" }}>
            CRS Credit Report Result
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
            <div>
              <strong>Credit Bureau Contact:</strong>
              <p style={{ margin: "5px 0", color: "#666" }}>
                {creditReportResult?.crsCreditReportResult?.responseDetail?.creditBureauContact?.name || "N/A"}
              </p>
            </div>
            <div>
              <strong>Bureau Address:</strong>
              <p style={{ margin: "5px 0", color: "#666" }}>
                {creditReportResult?.crsCreditReportResult?.responseDetail?.creditBureauContact?.address 
                  ? `${creditReportResult.crsCreditReportResult.responseDetail.creditBureauContact.address.addressLine1}, ${creditReportResult.crsCreditReportResult.responseDetail.creditBureauContact.address.city}, ${creditReportResult.crsCreditReportResult.responseDetail.creditBureauContact.address.state} ${creditReportResult.crsCreditReportResult.responseDetail.creditBureauContact.address.postalCode}`
                  : "N/A"}
              </p>
            </div>
          </div>

          {/* Tradelines Sub-section */}
          {creditReportResult?.crsCreditReportResult?.tradelines && creditReportResult.crsCreditReportResult.tradelines.length > 0 && (
            <div style={{ marginTop: "25px" }}>
              <h4 style={{ marginBottom: "15px", color: "#333", borderBottom: "1px solid #dee2e6", paddingBottom: "5px" }}>
                Credit Accounts (Tradelines)
              </h4>
              <div style={{ overflowX: "auto" }}>
                <table style={{ 
                  width: "100%", 
                  borderCollapse: "collapse", 
                  fontSize: "14px",
                  backgroundColor: "#fff",
                  border: "1px solid #dee2e6"
                }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f8f9fa" }}>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Creditor</th>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Account Type</th>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Status</th>
                      <th style={{ padding: "12px 8px", textAlign: "right", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Balance</th>
                      <th style={{ padding: "12px 8px", textAlign: "right", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Payment</th>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Rating</th>
                      <th style={{ padding: "12px 8px", textAlign: "center", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Lates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditReportResult.crsCreditReportResult.tradelines.map((tradeline, index) => (
                      <tr key={index} style={{ borderBottom: "1px solid #dee2e6" }}>
                            <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                              <div>
                                <div style={{ fontWeight: "500" }}>{tradeline.creditorName || "N/A"}</div>
                                <div style={{ fontSize: "12px", color: "#666" }}>ID: {tradeline.accountIdentifier || "N/A"}</div>
                                {tradeline.accountOwnershipType && (
                                  <div style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}>
                                    {tradeline.accountOwnershipType}
                                  </div>
                                )}
                              </div>
                            </td>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div>
                            <div>{tradeline.accountType || "N/A"}</div>
                            {tradeline.loanType && (
                              <div style={{ fontSize: "12px", color: "#666" }}>{tradeline.loanType}</div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div>
                            <div style={{ fontWeight: "500" }}>{tradeline.accountStatusType || "N/A"}</div>
                            {tradeline.accountClosedDate && (
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                Closed: {formatDate(tradeline.accountClosedDate)}
                              </div>
                            )}
                            {tradeline.accountOpenedDate && (
                              <div style={{ fontSize: "11px", color: "#888" }}>
                                Opened: {formatDate(tradeline.accountOpenedDate)}
                              </div>
                            )}
                          </div>
                        </td>
                            <td style={{ padding: "10px 8px", textAlign: "right", borderRight: "1px solid #dee2e6" }}>
                              <div>
                                <div style={{ fontWeight: "500" }}>
                                  {tradeline.currentBalanceAmount ? `$${formatCurrency(tradeline.currentBalanceAmount)}` : "N/A"}
                                </div>
                                {tradeline.creditLimitAmount && (
                                  <div style={{ fontSize: "12px", color: "#666" }}>
                                    Limit: ${formatCurrency(tradeline.creditLimitAmount)}
                                  </div>
                                )}
                                {tradeline.highBalanceAmount && (
                                  <div style={{ fontSize: "11px", color: "#888" }}>
                                    High: ${formatCurrency(tradeline.highBalanceAmount)}
                                  </div>
                                )}
                              </div>
                            </td>
                        <td style={{ padding: "10px 8px", textAlign: "right", borderRight: "1px solid #dee2e6" }}>
                          <div>
                            <div style={{ fontWeight: "500" }}>
                              {tradeline.monthlyPaymentAmount ? `$${formatCurrency(tradeline.monthlyPaymentAmount)}` : "N/A"}
                            </div>
                            {tradeline.termsDescription && (
                              <div style={{ fontSize: "12px", color: "#666" }}>{tradeline.termsDescription}</div>
                            )}
                            {tradeline.termsMonthsCount && (
                              <div style={{ fontSize: "11px", color: "#888" }}>
                                {tradeline.termsMonthsCount} months
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div>
                            <div style={{ 
                              color: tradeline.derogatoryDataIndicator ? "#dc3545" : "#28a745",
                              fontWeight: "500"
                            }}>
                              {tradeline.currentRatingType || "N/A"}
                            </div>
                            {tradeline.currentRatingCode && (
                              <div style={{ fontSize: "11px", color: "#888" }}>
                                Code: {tradeline.currentRatingCode}
                              </div>
                            )}
                            {tradeline.derogatoryDataIndicator && (
                              <div style={{ fontSize: "12px", color: "#dc3545", fontWeight: "500" }}>Derogatory</div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "10px 8px", textAlign: "center" }}>
                          <div style={{ fontSize: "12px" }}>
                            {tradeline._30DayLates && tradeline._30DayLates !== "0" && (
                              <div style={{ color: "#ffc107" }}>30: {tradeline._30DayLates}</div>
                            )}
                            {tradeline._60DayLates && tradeline._60DayLates !== "0" && (
                              <div style={{ color: "#fd7e14" }}>60: {tradeline._60DayLates}</div>
                            )}
                            {tradeline._90DayLates && tradeline._90DayLates !== "0" && (
                              <div style={{ color: "#dc3545" }}>90: {tradeline._90DayLates}</div>
                            )}
                            {tradeline._120DayLates && tradeline._120DayLates !== "0" && (
                              <div style={{ color: "#dc3545", fontWeight: "bold" }}>120+: {tradeline._120DayLates}</div>
                            )}
                            {(!tradeline._30DayLates || tradeline._30DayLates === "0") && 
                             (!tradeline._60DayLates || tradeline._60DayLates === "0") && 
                             (!tradeline._90DayLates || tradeline._90DayLates === "0") && (
                              <div style={{ color: "#28a745" }}>No Lates</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Inquiries Sub-section */}
          {creditReportResult?.crsCreditReportResult?.inquiries && creditReportResult.crsCreditReportResult.inquiries.length > 0 && (
            <div style={{ marginTop: "25px" }}>
              <h4 style={{ marginBottom: "15px", color: "#333", borderBottom: "1px solid #dee2e6", paddingBottom: "5px" }}>
                Credit Inquiries
              </h4>
              <div style={{ overflowX: "auto" }}>
                <table style={{ 
                  width: "100%", 
                  borderCollapse: "collapse", 
                  fontSize: "14px",
                  backgroundColor: "#fff",
                  border: "1px solid #dee2e6"
                }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f8f9fa" }}>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Creditor</th>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Inquiry Date</th>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Business Type</th>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Ownership</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditReportResult.crsCreditReportResult.inquiries.map((inquiry, index) => (
                      <tr key={index} style={{ borderBottom: "1px solid #dee2e6" }}>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div>
                            <div style={{ fontWeight: "500" }}>{inquiry.creditorName || "N/A"}</div>
                            {inquiry.subscriberCode && (
                              <div style={{ fontSize: "12px", color: "#666" }}>Code: {inquiry.subscriberCode}</div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div>
                            {formatDate(inquiry.inquiryDate)}
                          </div>
                        </td>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div>
                            {inquiry.businessType || "N/A"}
                          </div>
                        </td>
                        <td style={{ padding: "10px 8px" }}>
                          <div>
                            {inquiry.accountOwnershipType || "N/A"}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Employments Sub-section */}
          {creditReportResult?.crsCreditReportResult?.creditFiles?.[0]?.employments && creditReportResult.crsCreditReportResult.creditFiles[0].employments.length > 0 && (
            <div style={{ marginTop: "25px" }}>
              <h4 style={{ marginBottom: "15px", color: "#333", borderBottom: "1px solid #dee2e6", paddingBottom: "5px" }}>
                Employment History
              </h4>
              <div style={{ overflowX: "auto" }}>
                <table style={{ 
                  width: "100%", 
                  borderCollapse: "collapse", 
                  fontSize: "14px",
                  backgroundColor: "#fff",
                  border: "1px solid #dee2e6"
                }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f8f9fa" }}>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Employer</th>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Position</th>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Start Date</th>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>End Date</th>
                      <th style={{ padding: "12px 8px", textAlign: "right", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Monthly Income</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditReportResult.crsCreditReportResult.creditFiles[0].employments.map((employment, index) => (
                      <tr key={index} style={{ borderBottom: "1px solid #dee2e6" }}>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div style={{ fontWeight: "500" }}>{employment.employerName || "N/A"}</div>
                        </td>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div>{employment.employmentPositionDescription || "N/A"}</div>
                        </td>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div>
                            {employment.employmentStartDate ? formatDate(employment.employmentStartDate) : "N/A"}
                          </div>
                        </td>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div>
                            {employment.employmentEndDate ? formatDate(employment.employmentEndDate) : "Current"}
                          </div>
                        </td>
                        <td style={{ padding: "10px 8px", textAlign: "right" }}>
                          <div>
                            {employment.employmentMonthlyIncomeAmount ? `$${formatCurrency(employment.employmentMonthlyIncomeAmount)}` : "N/A"}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Response Alert Messages Sub-section */}
          {creditReportResult?.crsCreditReportResult?.responseAlertMessages && creditReportResult.crsCreditReportResult.responseAlertMessages.length > 0 && (
            <div style={{ marginTop: "25px" }}>
              <h4 style={{ marginBottom: "15px", color: "#333", borderBottom: "1px solid #dee2e6", paddingBottom: "5px" }}>
                Response Alert Messages
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {creditReportResult.crsCreditReportResult.responseAlertMessages.map((alert, index) => (
                  <div key={index} style={{ 
                    padding: "15px", 
                    backgroundColor: "#fff3cd", 
                    border: "1px solid #ffeaa7", 
                    borderRadius: "4px",
                    borderLeft: "4px solid #f39c12"
                  }}>
                    <div style={{ fontWeight: "500", marginBottom: "5px", color: "#856404" }}>
                      {alert.responseAlertMessageCategoryType || "Alert"}
                    </div>
                    <div style={{ color: "#856404", fontSize: "14px" }}>
                      {alert.responseAlertMessageText || "No message available"}
                    </div>
                    {alert.sourceType && (
                      <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "5px" }}>
                        Source: {alert.sourceType}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Score Factors Sub-section */}
          {creditReportResult?.crsCreditReportResult?.scores?.[0]?.scoreFactors && creditReportResult.crsCreditReportResult.scores[0].scoreFactors.length > 0 && (
            <div style={{ marginTop: "25px" }}>
              <h4 style={{ marginBottom: "15px", color: "#333", borderBottom: "1px solid #dee2e6", paddingBottom: "5px" }}>
                Score Factors
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {creditReportResult.crsCreditReportResult.scores[0].scoreFactors.map((factor, index) => (
                  <div key={index} style={{ 
                    padding: "12px", 
                    backgroundColor: "#f8f9fa", 
                    border: "1px solid #dee2e6", 
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}>
                    <div style={{ 
                      backgroundColor: "#6c757d", 
                      color: "white", 
                      borderRadius: "50%", 
                      width: "24px", 
                      height: "24px", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center", 
                      fontSize: "12px", 
                      fontWeight: "500",
                      flexShrink: 0
                    }}>
                      {factor.scoreFactorCode || (index + 1)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: "500", color: "#333" }}>
                        {factor.scoreFactorText || "Factor"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Public Records Sub-section */}
          {creditReportResult?.crsCreditReportResult?.publicRecords && creditReportResult.crsCreditReportResult.publicRecords.length > 0 && (
            <div style={{ marginTop: "25px" }}>
              <h4 style={{ marginBottom: "15px", color: "#333", borderBottom: "1px solid #dee2e6", paddingBottom: "5px" }}>
                Public Records
              </h4>
              <div style={{ overflowX: "auto" }}>
                <table style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                  backgroundColor: "#fff",
                  border: "1px solid #dee2e6"
                }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f8f9fa" }}>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Record Type</th>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Docket Number</th>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Filed Date</th>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Disposition</th>
                      <th style={{ padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Court</th>
                      <th style={{ padding: "12px 8px", textAlign: "right", borderBottom: "2px solid #dee2e6", fontWeight: "600" }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditReportResult.crsCreditReportResult.publicRecords.map((record, index) => (
                      <tr key={index} style={{ borderBottom: "1px solid #dee2e6" }}>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div>
                            <div style={{ fontWeight: "500" }}>{record.publicRecordType || "N/A"}</div>
                            {record.bankruptcyType && (
                              <div style={{ fontSize: "12px", color: "#666" }}>{record.bankruptcyType}</div>
                            )}
                            {record.consumerDisputeIndicator && (
                              <div style={{ fontSize: "11px", color: "#dc3545", fontWeight: "500" }}>Disputed</div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div>
                            <div style={{ fontWeight: "500" }}>{record.docketIdentifier || "N/A"}</div>
                            {record.subscriberCode && (
                              <div style={{ fontSize: "12px", color: "#666" }}>Code: {record.subscriberCode}</div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div>
                            {formatDate(record.filedDate)}
                          </div>
                        </td>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div>
                            <div style={{ fontWeight: "500" }}>{record.dispositionType || "N/A"}</div>
                            {record.dispositionDate && (
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                {formatDate(record.dispositionDate)}
                              </div>
                            )}
                            {record.settledDate && (
                              <div style={{ fontSize: "11px", color: "#888" }}>
                                Settled: {formatDate(record.settledDate)}
                              </div>
                            )}
                            {record.paidDate && (
                              <div style={{ fontSize: "11px", color: "#28a745" }}>
                                Paid: {formatDate(record.paidDate)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "10px 8px", borderRight: "1px solid #dee2e6" }}>
                          <div>
                            <div>{record.courtName || "N/A"}</div>
                            {record.attorneyName && (
                              <div style={{ fontSize: "12px", color: "#666" }}>Attorney: {record.attorneyName}</div>
                            )}
                            {record.plaintiffName && (
                              <div style={{ fontSize: "11px", color: "#888" }}>Plaintiff: {record.plaintiffName}</div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "10px 8px", textAlign: "right" }}>
                          <div>
                            {record.legalObligationAmount ? `$${formatCurrency(record.legalObligationAmount)}` : "N/A"}
                            {record.bankruptcyAssetsAmount && (
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                Assets: ${formatCurrency(record.bankruptcyAssetsAmount)}
                              </div>
                            )}
                            {record.bankruptcyLiabilitiesAmount && (
                              <div style={{ fontSize: "11px", color: "#888" }}>
                                Liabilities: ${formatCurrency(record.bankruptcyLiabilitiesAmount)}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer>
        <ActionButtons
          actions={[
            {
              text: "Cancel",
              onActionClick: () => {
                // Close the flyout - this will be handled by the parent component
                if (onExit) {
                  onExit();
                }
              },
              position: "left"
            },
            {
              text: "Download Credit Report PDF",
              onActionClick: downloadPdf,
              isPrimary: true,
              position: "right",
              isDisabled: () => {
                // Only enable if credit authorization is accepted
                return creditReportResult?.creditAuthorization?.decision !== 1; // 1 = Accept
              }
            }
          ]}
        />
      </Footer>
    </div>
  );
};
